/**
 * #19351: Client-side mirror of account-management's `PasswordSecurity.DEFAULT_RULES` (min. 10 characters, at least
 * 3 of 4 character groups, no part of the login/full name/e-mail address), so the profile dialog can tell while
 * typing what is still missing. The server stays authoritative — its 422 message wins.
 */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MIN_CHAR_GROUPS = 3;

export type CharGroup = "upper" | "lower" | "digit" | "symbol";

/** Same groups as `PasswordSecurity.CHARGROUP_*` (Java `\p{IsUppercase}`, `\p{IsLowercase}`, `\d`, rest). */
const CHAR_GROUPS: [CharGroup, RegExp][] = [
    ["upper", /\p{Uppercase}/u],
    ["lower", /\p{Lowercase}/u],
    ["digit", /\d/],
    ["symbol", /[^\d\p{Lowercase}\p{Uppercase}]/u],
];

/** Same as `PasswordSecurity.ALPHANUM_PART`: runs of at least 3 letters must not appear in the password. */
const PERSONAL_PART = /\p{Alphabetic}{3,}/gu;

export interface PasswordPersonalData {
    username?: string | null;
    fullName?: string | null;
    email?: string | null;
}

export interface PasswordCheck {
    lengthOk: boolean;
    /** Characters still missing up to {@link PASSWORD_MIN_LENGTH}. */
    missingLength: number;
    groupsOk: boolean;
    /** Groups not used yet, in display order. */
    missingGroups: CharGroup[];
    /** Groups still needed to reach {@link PASSWORD_MIN_CHAR_GROUPS}. */
    neededGroups: number;
    /** The first part of login/full name/e-mail (lowercase) found in the password, if any. */
    personalPart: string | null;
    ok: boolean;
}

function personalParts(data: PasswordPersonalData): string[] {
    return [data.username, data.fullName, data.email]
        .flatMap((value) => (value ? (value.match(PERSONAL_PART) ?? []) : []))
        .map((part) => part.toLowerCase());
}

export function checkPassword(password: string, data: PasswordPersonalData = {}): PasswordCheck {
    const missingGroups = CHAR_GROUPS.filter(([, re]) => !re.test(password)).map(([group]) => group);
    const usedGroups = CHAR_GROUPS.length - missingGroups.length;
    const lowered = password.toLowerCase();
    const personalPart = personalParts(data).find((part) => lowered.includes(part)) ?? null;
    const lengthOk = password.length >= PASSWORD_MIN_LENGTH;
    const groupsOk = usedGroups >= PASSWORD_MIN_CHAR_GROUPS;
    return {
        lengthOk,
        missingLength: Math.max(0, PASSWORD_MIN_LENGTH - password.length),
        groupsOk,
        missingGroups,
        neededGroups: Math.max(0, PASSWORD_MIN_CHAR_GROUPS - usedGroups),
        personalPart,
        ok: lengthOk && groupsOk && personalPart === null,
    };
}
